import * as React from 'react';
import { Invitation, InvitationStatus, MAX_INVITES_PER_ROLE, RoleType, User } from '@promo/shared';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { TokenizedEmailAutocomplete } from '@/components/email/TokenizedEmailAutocomplete';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export interface CoManagerEmailValidationOptions {
  duplicateEmails?: string[];
  existingInviteCount?: number;
  maxInvites?: number;
}

export interface CoManagerEmailValidationResult {
  emails: string[];
  error?: string;
}

interface CoManagersFieldProps {
  id?: string;
  coManagers: User[];
  pendingInvitations: Invitation[];
  disabled?: boolean;
  canInvite?: boolean;
  isInviting?: boolean;
  inviteInput?: string;
  validationError?: string;
  onInviteInputChange?: (value: string) => void;
  canRemove?: boolean;
  onRemoveCoManager?: (userId: string) => void;
  onRemovePendingInvitation?: (invitationId: string) => void;
  className?: string;
}

type CoManagerPillItem = {
  key: string;
  label: string;
  email: string;
  user?: User;
  pendingInvitations: Invitation[];
};

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export function parseCoManagerEmails(input: string): string[] {
  return input
    .split(/[,;\s]+/)
    .map(email => email.trim().toLowerCase())
    .filter(Boolean);
}

export function validateCoManagerEmails(
  input: string,
  {
    duplicateEmails = [],
    existingInviteCount = 0,
    maxInvites = MAX_INVITES_PER_ROLE[RoleType.MANAGER],
  }: CoManagerEmailValidationOptions = {}
): CoManagerEmailValidationResult {
  const emails = parseCoManagerEmails(input);

  if (emails.length === 0) {
    return { emails: [] };
  }

  const invalidEmails = emails.filter(email => {
    const isFormatValid = EMAIL_REGEX.test(email);
    const isDomainValid = email.endsWith('@tebra.com');
    return !isFormatValid || !isDomainValid;
  });
  if (invalidEmails.length > 0) {
    return { emails, error: `Invalid email format or domain: ${invalidEmails.join(', ')}` };
  }

  const duplicateInputEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
  if (duplicateInputEmails.length > 0) {
    return {
      emails,
      error: `Duplicate email: ${Array.from(new Set(duplicateInputEmails)).join(', ')}`,
    };
  }

  const blockedEmails = new Set(
    duplicateEmails.map(email => email.trim().toLowerCase()).filter(Boolean)
  );
  const blockedInputEmails = emails.filter(email => blockedEmails.has(email));
  if (blockedInputEmails.length > 0) {
    return { emails, error: `Already assigned or invited: ${blockedInputEmails.join(', ')}` };
  }

  const totalInvitationCount = existingInviteCount + emails.length;
  if (totalInvitationCount > maxInvites) {
    return {
      emails,
      error: `You can have at most ${maxInvites} co-managers. You're trying to invite ${emails.length} more, which would exceed the limit.`,
    };
  }

  return { emails };
}

export function CoManagersField({
  id,
  coManagers,
  pendingInvitations,
  disabled = false,
  canInvite = false,
  isInviting = false,
  inviteInput = '',
  validationError,
  onInviteInputChange,
  canRemove = false,
  onRemoveCoManager,
  onRemovePendingInvitation,
  className,
}: CoManagersFieldProps) {
  const [inviteEmails, setInviteEmails] = React.useState<string[]>(() =>
    parseCoManagerEmails(inviteInput)
  );
  const [inviteDraft, setInviteDraft] = React.useState('');
  const [invalidInviteTokens, setInvalidInviteTokens] = React.useState<string[]>([]);
  const inviteEmailsRef = React.useRef(inviteEmails);
  const inviteDraftRef = React.useRef('');
  const pendingCoManagerInvitations = pendingInvitations.filter(
    invitation => invitation.status === InvitationStatus.PENDING
  );
  const pendingInvitationsByEmail = pendingCoManagerInvitations.reduce((acc, invitation) => {
    const email = normalizeEmail(invitation.inviteeEmail);
    acc.set(email, [...(acc.get(email) ?? []), invitation]);
    return acc;
  }, new Map<string, Invitation[]>());
  const renderedPendingEmails = new Set<string>();
  const coManagerPillItems: CoManagerPillItem[] = coManagers.map(user => {
    const email = normalizeEmail(user.email);
    const matchingPendingInvitations = pendingInvitationsByEmail.get(email) ?? [];
    if (matchingPendingInvitations.length > 0) {
      renderedPendingEmails.add(email);
    }

    return {
      key: `user:${user.id}`,
      label: user.name || user.email,
      email: user.email,
      user,
      pendingInvitations: matchingPendingInvitations,
    };
  });

  for (const [email, invitations] of pendingInvitationsByEmail.entries()) {
    if (renderedPendingEmails.has(email)) continue;

    coManagerPillItems.push({
      key: `pending:${email}`,
      label: invitations[0]?.inviteeEmail ?? email,
      email: invitations[0]?.inviteeEmail ?? email,
      pendingInvitations: invitations,
    });
  }

  React.useEffect(() => {
    if (inviteInput.trim()) return;

    inviteEmailsRef.current = [];
    inviteDraftRef.current = '';
    setInviteEmails([]);
    setInviteDraft('');
    setInvalidInviteTokens([]);
  }, [inviteInput]);

  const composeInviteInput = (emails: string[], draft: string) =>
    [...emails, draft.trim().toLowerCase()].filter(Boolean).join(', ');

  const updateInviteInput = (emails: string[], draft: string) => {
    onInviteInputChange?.(composeInviteInput(emails, draft));
  };

  const handleInviteEmailsChange = (emails: string[]) => {
    inviteEmailsRef.current = emails;
    setInviteEmails(emails);
    setInvalidInviteTokens([]);
    updateInviteInput(emails, inviteDraftRef.current);
  };

  const handleInviteDraftChange = (draft: string) => {
    inviteDraftRef.current = draft;
    setInviteDraft(draft);
    setInvalidInviteTokens([]);
    updateInviteInput(inviteEmailsRef.current, draft);
  };

  const handleInvalidInviteTokensChange = (tokens: string[]) => {
    setInvalidInviteTokens(tokens);
    if (tokens.length > 0) {
      updateInviteInput(inviteEmailsRef.current, tokens.join(', '));
    }
  };

  const hasVisibleCoManagers = coManagerPillItems.length > 0;
  const renderedValidationError =
    invalidInviteTokens.length > 0
      ? `Invalid email format or domain: ${invalidInviteTokens.join(', ')}`
      : validationError;

  return (
    <div id={id} className={cn('space-y-2', className)}>
      <div className="flex min-h-[40px] flex-wrap items-center gap-2 py-2">
        {coManagerPillItems.map(item => {
          const isPending = item.pendingInvitations.length > 0;
          const title = isPending ? `${item.email} pending invitation` : item.email;
          const removeLabel = item.user
            ? `Remove co-manager ${item.label}`
            : `Remove pending co-manager invitation ${item.label}`;

          return (
            <Badge
              key={item.key}
              variant={isPending ? 'outline' : 'secondary'}
              title={title}
              aria-label={isPending ? `${item.label} pending invitation` : undefined}
              className={cn(
                'min-h-7 gap-1.5 px-3 py-1 pr-1.5 text-sm leading-none',
                isPending && 'border-muted-foreground/25 bg-muted/50 text-muted-foreground opacity-80'
              )}
            >
              <span>{item.label}</span>
              {canRemove ? (
                <button
                  type="button"
                  aria-label={removeLabel}
                  className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  onClick={() => {
                    if (item.user) {
                      onRemoveCoManager?.(item.user.id);
                    }
                    item.pendingInvitations.forEach(invitation => {
                      onRemovePendingInvitation?.(invitation.id);
                    });
                  }}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              ) : (
                <span
                  aria-hidden="true"
                  className="invisible ml-0.5 flex h-5 w-5 items-center justify-center"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
            </Badge>
          );
        })}
        {!hasVisibleCoManagers && (
          <span className="text-base text-muted-foreground">None</span>
        )}
      </div>

      {canInvite && (
        <div className="space-y-1">
          <div className="flex items-start gap-2">
            <TokenizedEmailAutocomplete
              id={id ? `${id}-emails` : 'co-manager-emails'}
              ariaLabel="Co-manager emails"
              value={inviteEmails}
              draftValue={inviteDraft}
              onChange={handleInviteEmailsChange}
              onDraftChange={handleInviteDraftChange}
              onInvalidTokensChange={handleInvalidInviteTokensChange}
              placeholder="name@tebra.com, other@tebra.com"
              disabled={disabled || isInviting}
            />
          </div>
          {renderedValidationError && (
            <p className="text-sm text-destructive">{renderedValidationError}</p>
          )}
        </div>
      )}
    </div>
  );
}
